import { useQuery } from '@tanstack/react-query';
import { Text, Grid, Card, Badge, Metric, BarList } from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import { getRetencaoEmail, extractErrorMessage } from '@/lib/api';

interface FunnelStep {
  step: string;
  n: number | null;
  rate: number | null;
}
interface EngagedSide {
  n: number | null;
  retorno_7d: number | null;
}
interface LeadingIndicator {
  signal: string;
  n: number | null;
  p_with: number | null;
  p_without: number | null;
  lift: number | null;
}
interface EmailData {
  email_funnel: { steps: FunnelStep[]; sent: number | null };
  engaged_retention: { abriram: EngagedSide; nao_abriram: EngagedSide };
  leading_indicators: LeadingIndicator[];
  dormancy: {
    dormentes_14d: number | null;
    reativados_7d: number | null;
    nota: string;
  };
  channel_health: {
    sent: number | null;
    delivered: number | null;
    bounced: number | null;
    complained: number | null;
    bounce_rate: number | null;
    complaint_rate: number | null;
  };
}

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}
function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

export function EmailEngagementSection() {
  const { data, isLoading, error } = useQuery<EmailData>({
    queryKey: ['retencao-email'],
    queryFn: async () => (await getRetencaoEmail()).data,
    refetchInterval: 60_000,
  });
  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar engajamento de email')
    : null;

  const funnelItems = (data?.email_funnel.steps ?? []).map((s) => ({
    name: `${s.step} · ${fmtPct(s.rate)}`,
    value: s.n ?? 0,
  }));

  const liftItems = (data?.leading_indicators ?? [])
    .filter((l) => l.lift !== null && l.lift !== undefined)
    .map((l) => ({ name: `${l.signal} (n=${fmtInt(l.n)})`, value: l.lift as number }));

  const er = data?.engaged_retention;
  const dz = data?.dormancy;
  const ch = data?.channel_health;

  return (
    <div className="space-y-6">
      <header className="space-y-1 border-t border-slate-800 pt-6">
        <div className="flex items-center justify-between">
          <Text className="text-sm font-semibold uppercase tracking-wide text-slate-300">
            Engajamento por email (tracking Resend)
          </Text>
          {data && (
            <Badge color="emerald" size="xs">
              {fmtInt(data.channel_health.sent)} eventos
            </Badge>
          )}
        </div>
        <Text className="text-xs text-slate-400">
          Mede o trabalho do email: email → ação → retorno → retenção. Não opens
          soltos.
        </Text>
      </header>

      {/* 1. Funil do email caso-pronto */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          1 · Funil do email "caso pronto"
        </Text>
        <Card>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && funnelItems.length > 0 && (
            <BarList
              data={funnelItems}
              valueFormatter={(v: number) =>
                new Intl.NumberFormat('pt-BR').format(v)
              }
              color="emerald"
            />
          )}
          {!isLoading && !errMsg && funnelItems.length === 0 && (
            <Text className="text-sm text-slate-400">
              sem emails caso-pronto rastreados ainda
            </Text>
          )}
        </Card>
      </section>

      {/* 2. Retenção: abriram × não abriram */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          2 · Retenção — quem abriu o email × quem não (retorno em 7d)
        </Text>
        <Grid numItems={1} numItemsSm={2} className="gap-4">
          <Card className="border border-emerald-700/40 bg-emerald-900/10">
            <Text className="text-emerald-200">Abriram o email</Text>
            <Metric className="text-emerald-300">
              {fmtPct(er?.abriram.retorno_7d)}
            </Metric>
            <Text className="text-xs text-slate-400">
              retornaram em 7d · n={fmtInt(er?.abriram.n)}
            </Text>
          </Card>
          <Card>
            <Text className="text-slate-300">Não abriram</Text>
            <Metric className="text-slate-400">
              {fmtPct(er?.nao_abriram.retorno_7d)}
            </Metric>
            <Text className="text-xs text-slate-400">
              retornaram em 7d · n={fmtInt(er?.nao_abriram.n)}
            </Text>
          </Card>
        </Grid>
      </section>

      {/* 3. Indicadores antecedentes de retenção */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          3 · Indicadores antecedentes (comportamento semana 1 → 2º cálculo)
        </Text>
        <Card>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && !errMsg && liftItems.length > 0 && (
            <>
              <div className="mb-2 flex items-center justify-between">
                <Text className="text-xs uppercase tracking-wide text-slate-400">
                  sinal
                </Text>
                <Text className="text-xs uppercase tracking-wide text-slate-400">
                  lift (× repetir)
                </Text>
              </div>
              <BarList
                data={liftItems}
                valueFormatter={(v: number) => `${v.toFixed(1)}×`}
                color="blue"
              />
            </>
          )}
          {!isLoading && !errMsg && liftItems.length === 0 && (
            <Text className="text-sm text-slate-400">sem dados suficientes</Text>
          )}
        </Card>
      </section>

      {/* 4 + 5: Dormência e Saúde do canal */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          4 · Dormência → reativação &nbsp;·&nbsp; 5 · Saúde do canal
        </Text>
        <Grid numItems={1} numItemsLg={2} className="gap-4">
          <Card className="border border-amber-700/40 bg-amber-900/10">
            <Text className="text-amber-200">Dormentes (14d sem atividade)</Text>
            <Metric className="text-amber-300">{fmtInt(dz?.dormentes_14d)}</Metric>
            <Text className="text-xs text-slate-400">
              reativados em 7d: {fmtInt(dz?.reativados_7d)}
            </Text>
            <Text className="pt-1 text-xs text-amber-100/70">{dz?.nota}</Text>
          </Card>
          <Card>
            <Text className="text-slate-300">Entregabilidade</Text>
            <Grid numItems={2} className="mt-2 gap-2">
              <KPICard
                label="Bounce"
                value={isLoading ? null : fmtPct(ch?.bounce_rate)}
                loading={isLoading}
                error={errMsg}
                hint={`${fmtInt(ch?.bounced)} de ${fmtInt(ch?.sent)} enviados`}
              />
              <KPICard
                label="Reclamação (spam)"
                value={isLoading ? null : fmtPct(ch?.complaint_rate)}
                loading={isLoading}
                error={errMsg}
                hint={`${fmtInt(ch?.complained)} reclamações`}
              />
            </Grid>
          </Card>
        </Grid>
      </section>
    </div>
  );
}
